/**
 * Determines whether to skip the AI response fetch based on visibility state.
 *
 * @param i - The index of the AI example being clicked
 * @param isUserInitiated - Whether the click was initiated by the user (vs automatic prefetch)
 * @param currentResponses - The current responses array
 * @param aiExampleVisible - Record tracking which AI examples are visible
 * @returns Object with skip (whether to skip the fetch) and updateVisible (whether to mark as visible)
 */
export const shouldSkipAiResponse = (
	i: number,
	isUserInitiated: boolean,
	currentResponses: string[][],
	aiExampleVisible: Record<number, boolean>
): { skip: boolean; updateVisible: boolean } => {
	// Automatic fetches (prefetch) should never be skipped
	if (!isUserInitiated) {
		return { skip: false, updateVisible: false }
	}

	// For user-initiated clicks on indices > 0, check if previous response is visible
	if (i > 0 && (!currentResponses[i - 1] || !aiExampleVisible[i - 1])) {
		return { skip: true, updateVisible: false }
	}

	// If this example isn't visible yet, mark it as visible but skip the fetch
	// (the prefetched data will be used instead)
	if (!aiExampleVisible[i]) {
		return { skip: true, updateVisible: true }
	}

	// Otherwise, proceed with the fetch
	return { skip: false, updateVisible: false }
}
